/**
 * Core API Service Layer for Litla Gámaleigan Admin Dashboard
 * Provides shared infrastructure: CSRF token management, authentication, and base fetchApi
 */

import { logger } from '../utils/logger';

// API base URL - uses production domain by default, overrideable via VITE_API_URL env var
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.gamaleigan.is';

// ============================================
// CSRF Token Management
// ============================================

/**
 * CSRF Token state management
 * Token stored in memory (not localStorage for security)
 */
let csrfToken: string | null = null;
let tokenExpiry: number = 0;
let tokenFetchPromise: Promise<string> | null = null;

/**
 * Get CSRF token - fetches from backend or returns cached token
 * Implements deduplication to prevent multiple simultaneous fetches
 *
 * @returns Promise<string> The CSRF token
 * @throws Error if token fetch fails
 */
export async function getCsrfToken(): Promise<string> {
  // Return cached token if still valid
  if (csrfToken && Date.now() < tokenExpiry) {
    return csrfToken;
  }

  // If fetch already in progress, return existing promise (deduplication)
  if (tokenFetchPromise) {
    return tokenFetchPromise;
  }

  // Create new fetch promise
  tokenFetchPromise = (async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/auth/csrf-token`, {
        credentials: 'include', // Required for cross-origin cookies
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json();
      csrfToken = data.token;
      tokenExpiry = data.expiresAt;

      logger.log('[CSRF] Token fetched successfully, expires at:', new Date(tokenExpiry).toISOString());
      return csrfToken;
    } catch (error) {
      logger.error('[CSRF] Failed to fetch token:', error);
      throw error;
    } finally {
      // Clear fetch promise after completion (success or failure)
      tokenFetchPromise = null;
    }
  })();

  return tokenFetchPromise;
}

/**
 * Initialize CSRF token on app load
 * Called from App.tsx or main.tsx
 *
 * @returns Promise<void>
 */
export async function initializeCsrfToken(): Promise<void> {
  try {
    await getCsrfToken();
    logger.log('[CSRF] Token initialized on app load');
  } catch (error) {
    logger.warn('[CSRF] Failed to initialize token on app load:', error);
    // Non-critical: token will be fetched on first mutating request
  }
}

/**
 * Invalidate cached CSRF token
 * Used when receiving 403 CSRF errors to force token refresh
 */
export function invalidateCsrfToken(): void {
  csrfToken = null;
  tokenExpiry = 0;
  logger.log('[CSRF] Token invalidated, will refresh on next request');
}

// ============================================
// Base API Fetch Helper
// ============================================

/**
 * Base fetch helper for API requests with CSRF protection
 * Handles authentication headers, CSRF tokens, and automatic retries
 *
 * @param endpoint - API endpoint path (e.g., '/api/gamar')
 * @param options - Fetch options (method, body, headers)
 * @param retryCount - Internal retry counter (don't set manually)
 * @returns Promise with parsed JSON response
 * @throws Error if request fails
 */
export async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {},
  retryCount: number = 0
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const method = options.method || 'GET';
  const isMutatingRequest = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase());

  // Get JWT token from localStorage
  const token = localStorage.getItem('auth_token');

  logger.log(`[API] ${method} ${endpoint}`, {
    hasToken: !!token,
    tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
  });

  // Build headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers as Record<string, string>,
  };

  // Add CSRF token for mutating requests
  if (isMutatingRequest) {
    try {
      const csrf = await getCsrfToken();
      headers['X-CSRF-Token'] = csrf;
      logger.log(`[API] CSRF token added for ${method} request`);
    } catch (error) {
      logger.error('[CSRF] Failed to get token for request:', error);
      // Continue without CSRF token - backend will reject with 403
    }
  }

  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Required for cross-origin cookies (CSRF)
    headers,
  });

  logger.log(`[API] Response: ${response.status} ${response.statusText} for ${endpoint}`);

  // Handle 401 Unauthorized - clear token and throw (let caller handle redirect)
  if (response.status === 401) {
    logger.error(`[API] 401 Unauthorized for ${endpoint} - clearing auth state`);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    // Don't redirect here - let PrivateRoute handle navigation
    // This allows AuthContext to gracefully handle token validation failures
    throw new Error('Unauthorized');
  }

  // Handle 403 CSRF errors with automatic retry
  if (response.status === 403 && retryCount === 0) {
    const error = await response.json().catch(() => ({ error: 'Forbidden' }));

    // Check if this is a CSRF error
    if (error.error?.includes('CSRF') || error.error?.includes('csrf')) {
      logger.warn('[CSRF] Token rejected (403), invalidating and retrying...');
      invalidateCsrfToken();

      // Retry the request once with fresh token
      return fetchApi<T>(endpoint, options, retryCount + 1);
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `API Error: ${response.status}`);
  }

  return response.json();
}

// ============================================
// Authentication API
// ============================================

/**
 * Authentication API endpoints
 * Handles login, logout, and user session management
 */
export const authApi = {
  /**
   * Login with email and password
   * @param email - User email
   * @param password - User password
   * @param retryCount - Internal retry counter (don't set manually)
   * @returns Promise with auth token and user details
   */
  async login(email: string, password: string, retryCount: number = 0): Promise<{
    token: string;
    user: {
      id: number;
      email: string;
      full_name: string;
      role: string;
    };
  }> {
    // Get CSRF token for login request
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    try {
      const csrf = await getCsrfToken();
      headers['X-CSRF-Token'] = csrf;
    } catch (error) {
      logger.error('[CSRF] Failed to get token for login:', error);
      // Continue without CSRF token - backend will reject with 403
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
      method: 'POST',
      credentials: 'include', // Required for cross-origin cookies (CSRF)
      headers,
      body: JSON.stringify({ email, password }),
    });

    // Handle 403 CSRF errors with automatic retry
    if (response.status === 403 && retryCount === 0) {
      const error = await response.json().catch(() => ({ error: 'Forbidden' }));

      // Check if this is a CSRF error
      if (error.error?.includes('CSRF') || error.error?.includes('csrf')) {
        logger.warn('[CSRF] Token rejected (403) on login, invalidating and retrying...');
        invalidateCsrfToken();

        // Retry the login once with fresh token
        return authApi.login(email, password, retryCount + 1);
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      throw new Error(error.error || 'Invalid credentials');
    }

    const result = await response.json();
    // Backend wraps response in { success, data }
    return result.data || result;
  },

  /**
   * Logout current user
   * Clears auth token and optionally notifies backend
   */
  async logout(): Promise<void> {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Get CSRF token for logout request
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };

      try {
        const csrf = await getCsrfToken();
        headers['X-CSRF-Token'] = csrf;
      } catch (error) {
        logger.warn('[CSRF] Failed to get token for logout, proceeding anyway:', error);
      }

      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Required for cross-origin cookies (CSRF)
        headers,
      }).catch(() => {});
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  },

  /**
   * Get current authenticated user
   * @returns Promise with user details
   */
  async getCurrentUser(): Promise<{
    id: number;
    email: string;
    full_name: string;
    role: string;
  }> {
    const response = await fetchApi<{ user: { id: number; email: string; name: string; role: string } }>('/api/auth/me');
    // Map backend 'name' field to frontend 'full_name' field
    return {
      id: response.user.id,
      email: response.user.email,
      full_name: response.user.name,
      role: response.user.role,
    };
  },
};

// ============================================
// Re-export all domain APIs
// ============================================

export { containerApi } from './containerApi';
export { customerApi } from './customerApi';
export { orderApi } from './orderApi';
export { invoiceApi } from './invoiceApi';
export { cmsApi, markdownApi } from './cmsApi';
export { supportApi } from './supportApi';
export { vehicleApi } from './vehicleApi';
export { dashboardApi, healthApi } from './analyticsApi';
